import { BreakpointObserver } from '@angular/cdk/layout';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type SidebarMode = 'minimized' | 'expanded-overlay' | 'side-by-side' | 'drawer';

export interface SidebarState {
  isMinimized: boolean;
  isHovered: boolean;
  isSideBySide: boolean;
  isDrawerOpen: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  settingsDrawerOpen: boolean;
  activeMenuLabel?: string;
  activeParentMenuLabel?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SidebarStateService {
  private _state = new BehaviorSubject<SidebarState>({
    isMinimized: true, // Default to minimized
    isHovered: false,
    isSideBySide: false,
    isDrawerOpen: false,
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    settingsDrawerOpen: false,
  });

  public state$ = this._state.asObservable();

  constructor(private breakpointObserver: BreakpointObserver) {
    this.initBreakpointObserver();
  }

  get currentState(): SidebarState {
    return this._state.value;
  }

  setHovered(isHovered: boolean): void {
    const currentState = this._state.value;
    if (currentState.isHovered !== isHovered) {
      this._state.next({
        ...currentState,
        isHovered,
      });
    }
  }

  toggleSideBySide(): void {
    const currentState = this._state.value;
    if (currentState.isSideBySide) {
      // If currently in side-by-side mode, return to minimized
      this._state.next({
        ...currentState,
        isSideBySide: false,
        isMinimized: true,
        isHovered: false, // Clear hover state when returning to minimized
      });
    } else {
      // If not in side-by-side mode, enable it
      this._state.next({
        ...currentState,
        isSideBySide: true,
        isMinimized: false,
        isHovered: false, // Clear hover state when entering side-by-side
      });
    }
  }

  setMinimized(isMinimized: boolean): void {
    const currentState = this._state.value;
    const newState = {
      ...currentState,
      isMinimized,
      isSideBySide: false, // Reset side-by-side when setting minimized
    };

    // Only emit if state actually changed
    if (JSON.stringify(currentState) !== JSON.stringify(newState)) {
      this._state.next(newState);
    }
  }

  toggleDrawer(): void {
    const currentState = this._state.value;
    this._state.next({
      ...currentState,
      isDrawerOpen: !currentState.isDrawerOpen,
    });
  }

  setDrawerOpen(isOpen: boolean): void {
    const currentState = this._state.value;
    this._state.next({
      ...currentState,
      isDrawerOpen: isOpen,
    });
  }

  setActiveMenuLabel(label: string, parentLabel?: string): void {
    const currentState = this._state.value;
    if (
      currentState.activeMenuLabel === label &&
      currentState.activeParentMenuLabel === parentLabel
    ) {
      return;
    }
    this._state.next({
      ...currentState,
      activeMenuLabel: label,
      activeParentMenuLabel: parentLabel,
    });
  }

  setSettingsDrawerOpen(isOpen: boolean): void {
    const currentState = this._state.value;
    this._state.next({
      ...currentState,
      settingsDrawerOpen: isOpen,
    });
  }

  toggleSettingsDrawer(): void {
    const currentState = this._state.value;
    this._state.next({
      ...currentState,
      settingsDrawerOpen: !currentState.settingsDrawerOpen,
    });
  }

  private initBreakpointObserver(): void {
    // Mobile breakpoint (< 768px)
    this.breakpointObserver.observe(['(max-width: 767px)']).subscribe(result => {
      const currentState = this._state.value;
      if (result.matches) {
        // Mobile mode: force drawer mode
        this._state.next({
          ...currentState,
          isMobile: true,
          isTablet: false,
          isDesktop: false,
          isMinimized: true,
          isSideBySide: false,
          isHovered: false,
          isDrawerOpen: false,
        });
      }
    });

    this.breakpointObserver
      .observe(['(min-width: 768px) and (max-width: 1199px)'])
      .subscribe(result => {
        const currentState = this._state.value;
        if (result.matches) {
          this._state.next({
            ...currentState,
            isMobile: false,
            isTablet: true,
            isDesktop: false,
            isMinimized: true,
            isSideBySide: false,
            isDrawerOpen: false,
          });
        }
      });

    this.breakpointObserver.observe(['(min-width: 1200px)']).subscribe(result => {
      const currentState = this._state.value;
      if (result.matches) {
        this._state.next({
          ...currentState,
          isMobile: false,
          isTablet: false,
          isDesktop: true,
          isDrawerOpen: false,
        });
      }
    });
  }
}
